<template>
  <div class="page-organization-edit">
    <!-- Loader -->
    <div v-if="isLoading" class="has-text-centered mt-6">
      <span class="loader is-loading is-large"></span>
    </div>

    <div v-else>
      <!-- Breadcrumb -->
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><router-link to="/dashboard">Dashboard</router-link></li>
          <li class="is-active">
            <a aria-current="page">Organization settings</a>
          </li>
        </ul>
      </nav>

      <h1 class="title">Edit organization</h1>
      <h2 class="subtitle">{{ form.name }}</h2>

      <form @submit.prevent="submitForm">
        <div class="columns">
          <!-- Left column -->
          <div class="column is-half">
            <h3 class="title is-5">General information</h3>

            <div class="field">
              <label class="label">Name</label>
              <div class="control">
                <input v-model="form.name" class="input" type="text" required />
              </div>
            </div>

            <div class="field">
              <label class="label">Legal name</label>
              <div class="control">
                <input
                  v-model="form.legal_name"
                  class="input"
                  type="text"
                  placeholder="Legal company name"
                />
              </div>
            </div>

            <div class="field is-horizontal">
              <div class="field-body">
                <div class="field">
                  <label class="label">Org number</label>
                  <div class="control">
                    <input
                      v-model="form.org_number"
                      class="input"
                      type="text"
                      placeholder="Company number / CBE"
                    />
                  </div>
                </div>

                <div class="field">
                  <label class="label">VAT number</label>
                  <div class="control">
                    <input
                      v-model="form.vat_number"
                      class="input"
                      type="text"
                      placeholder="VAT number"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="field">
              <label class="label">Email</label>
              <div class="control">
                <input
                  v-model="form.email"
                  class="input"
                  type="email"
                  placeholder="contact@company.com"
                />
              </div>
            </div>

            <div class="field">
              <label class="label">Website</label>
              <div class="control">
                <input
                  v-model="form.website"
                  class="input"
                  type="text"
                  placeholder="https://example.com"
                />
              </div>
            </div>

            <hr />

            <h3 class="title is-5">Address</h3>

            <div class="field">
              <label class="label">Address line 1</label>
              <div class="control">
                <input v-model="form.address1" class="input" type="text" />
              </div>
            </div>

            <div class="field">
              <label class="label">Address line 2</label>
              <div class="control">
                <input v-model="form.address2" class="input" type="text" />
              </div>
            </div>

            <div class="field is-horizontal">
              <div class="field-body">
                <div class="field">
                  <label class="label">Zip code</label>
                  <div class="control">
                    <input v-model="form.zipcode" class="input" type="text" />
                  </div>
                </div>

                <div class="field">
                  <label class="label">City</label>
                  <div class="control">
                    <input v-model="form.place" class="input" type="text" />
                  </div>
                </div>
              </div>
            </div>

            <div class="field is-horizontal">
              <div class="field-body">
                <div class="field">
                  <label class="label">Region</label>
                  <div class="control">
                    <input v-model="form.region" class="input" type="text" />
                  </div>
                </div>

                <div class="field">
                  <label class="label">Country code</label>
                  <div class="control">
                    <input
                      v-model="form.country_code"
                      class="input"
                      type="text"
                      maxlength="2"
                      placeholder="BE, FR, ..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right column -->
          <div class="column is-half">
            <h3 class="title is-5">Bank & payments</h3>

            <div class="field">
              <label class="label">Bank account (legacy)</label>
              <div class="control">
                <input v-model="form.bankaccount" class="input" type="text" />
              </div>
            </div>

            <div class="field">
              <label class="label">IBAN</label>
              <div class="control">
                <input v-model="form.iban" class="input" type="text" />
              </div>
            </div>

            <div class="field">
              <label class="label">BIC</label>
              <div class="control">
                <input v-model="form.bic" class="input" type="text" />
              </div>
            </div>

            <div class="field is-horizontal">
              <div class="field-body">
                <div class="field">
                  <label class="label">Currency</label>
                  <div class="control">
                    <input
                      v-model="form.currency"
                      class="input"
                      type="text"
                      maxlength="3"
                    />
                  </div>
                </div>

                <div class="field">
                  <label class="label">Payment means code</label>
                  <div class="control">
                    <input
                      v-model="form.payment_means_code"
                      class="input"
                      type="text"
                    />
                  </div>
                </div>
              </div>
            </div>

            <hr />

            <h3 class="title is-5">Peppol / E-invoicing</h3>

            <div class="field">
              <label class="label">Peppol endpoint</label>
              <div class="control">
                <input
                  v-model="form.peppol_endpoint"
                  class="input"
                  type="text"
                  placeholder="Peppol EndpointID"
                />
              </div>
            </div>

            <div class="field">
              <label class="label">Peppol scheme</label>
              <div class="control">
                <input
                  v-model="form.peppol_scheme"
                  class="input"
                  type="text"
                  placeholder="0088, 9938, 0208, ..."
                />
              </div>
            </div>

            <hr />

            <h3 class="title is-5">Logo</h3>

            <div class="field">
              <div class="control">
                <div class="file has-name is-fullwidth">
                  <label class="file-label">
                    <input
                      class="file-input"
                      type="file"
                      name="logo"
                      accept="image/*"
                      @change="onLogoChange"
                    />
                    <span class="file-cta">
                      <span class="file-label"> Choose a logo… </span>
                    </span>
                    <span class="file-name">
                      {{ logoName || 'No file selected' }}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <p v-if="form.logo" class="mt-2">
              <strong>Current logo:</strong>
              <span>{{ shortLogoPath }}</span>
            </p>
          </div>
        </div>

        <div class="field is-grouped mt-5">
          <div class="control">
            <button
              class="button is-primary"
              type="submit"
              :disabled="isSaving"
            >
              <span
                v-if="isSaving"
                class="loader is-loading is-small mr-2"
              ></span>
              <span>Save changes</span>
            </button>
          </div>
          <div class="control">
            <button
              type="button"
              class="button is-light"
              @click="resetForm"
              :disabled="isSaving"
            >
              Reset
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import api from '@/utils/axios';
import { toast } from 'bulma-toast';

export default {
  name: 'EditOrganization',
  data() {
    return {
      isLoading: false,
      isSaving: false,
      originalData: null, // Used to reset the form
      form: {
        id: null,
        name: '',
        org_number: '',
        first_document_number: null,
        bankaccount: '',
        email: '',
        address1: '',
        address2: '',
        zipcode: '',
        place: '',
        region: '',
        country_code: '',
        iban: '',
        bic: '',
        payment_means_code: '58',
        website: '',
        logo: null,
        currency: 'EUR',
        peppol_endpoint: '',
        peppol_scheme: '',
        legal_name: '',
        vat_number: '',
        recommand_company_id: '',
      },
      logoFile: null,
    };
  },
  computed: {
    logoName() {
      // Display selected file name
      return this.logoFile ? this.logoFile.name : '';
    },
    shortLogoPath() {
      // Display a short version of current logo path
      if (!this.form.logo) return '';
      const parts = String(this.form.logo).split('/');
      return parts[parts.length - 1];
    },
  },
  created() {
    // Fetch organization using same logic as legacy page
    this.fetchOrganization();
  },
  methods: {
    withDefaults(org) {
      // Ensure Peppol/legal fields exist and are reactive
      return {
        ...org,
        legal_name: org.legal_name ?? '',
        vat_number: org.vat_number ?? '',
        peppol_endpoint: org.peppol_endpoint ?? '',
        peppol_scheme: org.peppol_scheme ?? '9930',
        country_code: (org.country_code ?? 'BE').toUpperCase(),
      };
    },
    async fetchOrganization() {
      // Load first organization from backend (same logic as old component)
      this.isLoading = true;
      try {
        const { data } = await api.get('organizations/');
        const org = data?.[0];
        if (!org) {
          toast({
            message: 'No organization found.',
            type: 'is-warning',
            duration: 3000,
            position: 'bottom-right',
          });
          return;
        }
        const withDefaults = this.withDefaults(org);
        this.form = {
          ...this.form,
          ...withDefaults,
          id: withDefaults.id,
        };
        this.originalData = { ...this.form };
      } catch (error) {
        console.error('Error loading organization', error);
        toast({
          message: 'Failed to load organization',
          type: 'is-danger',
          duration: 3000,
          position: 'bottom-right',
        });
      } finally {
        this.isLoading = false;
      }
    },
    onLogoChange(event) {
      // Handle logo file input change
      const file = event.target.files[0];
      if (!file) {
        this.logoFile = null;
        return;
      }
      this.logoFile = file;
    },
    resetForm() {
      // Reset form to last loaded data
      if (!this.originalData) return;
      this.form = { ...this.originalData };
      this.logoFile = null;
    },
    buildPayload() {
      // Build clean payload to avoid sending read-only or unwanted fields
      const o = this.form || {};
      return {
        name: o.name ?? null,
        org_number: o.org_number ?? null,
        first_document_number: o.first_document_number ?? null,
        bankaccount: o.bankaccount ?? null,
        email: o.email ?? null,
        address1: o.address1 ?? null,
        address2: o.address2 ?? null,
        zipcode: o.zipcode ?? null,
        place: o.place ?? null,
        region: o.region ?? null,
        website: o.website ?? null,
        currency: o.currency ?? 'EUR',
        country_code: (o.country_code || 'BE').toUpperCase(),
        iban: o.iban ?? null,
        bic: o.bic ?? null,
        payment_means_code: o.payment_means_code ?? '58',
        // Peppol / legal
        legal_name: o.legal_name || null,
        vat_number: o.vat_number || null,
        peppol_endpoint: o.peppol_endpoint || null,
        peppol_scheme: o.peppol_scheme || null,
        // Recommand
        recommand_company_id: o.recommand_company_id || null,
      };
    },
    warnIfPeppolIncomplete() {
      // Tiny UX helper: warn if endpoint/scheme mismatch
      const { peppol_endpoint: ep, peppol_scheme: sc } = this.form || {};
      if ((ep && !sc) || (!ep && sc)) {
        toast({
          message:
            'Peppol: provide both Endpoint and Scheme for best compatibility.',
          type: 'is-warning',
          dismissible: true,
          duration: 3000,
          position: 'bottom-right',
        });
      }
    },
    async submitForm() {
      // Submit updated organization data to backend using existing endpoint
      if (!this.form.id) {
        toast({
          message: 'Missing organization id.',
          type: 'is-danger',
          duration: 3000,
          position: 'bottom-right',
        });
        return;
      }

      this.isSaving = true;

      try {
        this.warnIfPeppolIncomplete();
        const payload = this.buildPayload();

        let dataToSend = payload;
        const config = {};

        // If logo is selected, use multipart/form-data
        if (this.logoFile) {
          const formData = new FormData();
          Object.keys(payload).forEach((key) => {
            const value = payload[key];
            if (value !== null && value !== undefined) {
              formData.append(key, value);
            }
          });
          formData.append('logo', this.logoFile);
          dataToSend = formData;
          config.headers = {
            'Content-Type': 'multipart/form-data',
          };
        }

        const response = await api.patch(
          `/organizations/${this.form.id}/`,
          dataToSend,
          config
        );

        this.form = {
          ...this.form,
          ...response.data,
        };
        this.originalData = { ...this.form };
        this.logoFile = null;

        toast({
          message: 'Organization updated successfully',
          type: 'is-success',
          duration: 3000,
          position: 'bottom-right',
        });
      } catch (error) {
        console.error('Error updating organization', error);
        let message = 'Failed to update organization';
        if (error.response && error.response.data) {
          const data = error.response.data;
          const firstKey = Object.keys(data)[0];
          if (firstKey && Array.isArray(data[firstKey])) {
            message += `: ${data[firstKey][0]}`;
          }
        }
        toast({
          message,
          type: 'is-danger',
          duration: 4000,
          position: 'bottom-right',
        });
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.page-organization-edit {
  padding: 1.5rem;
}
</style>
